/* eslint-disable no-lonely-if */
/* eslint-disable no-else-return */
import { createSelector } from 'reselect';
import * as R from 'ramda';

import * as actionTypes from 'constants/Payments';
import * as loginTypes from 'constants/Login';
import { add, round } from 'utils';
import {
  getDefaultCurrency,
  getPosRoundCashFunction,
} from 'reducers/configs/settings';
import { getLoggedInEmployeeID } from 'reducers/Login';
import { getActivePaymentIntegration } from 'reducers/cafaConfigs';
import { getDefaultCustomer } from 'reducers/customerSearch';

import { getCurrencyByCode } from './configs/currency';
import { createOverrideSelector } from './Plugins';

const initialState = {
  isCurrentSaleAReturn: false,
  cumulativeSumOfRoundings: 0,
  currencies: [],
  currentSalesDocument: {},
  employeeID: 0,
  originalPayments: [],
  returnPayments: [],
  selectedCustomer: {},
  selectedPos: {},
  shoppingCartTotal: 0,
  type: '',
  salesDocument: {},
  total: null,
  payments: {},
  customer: {},
  resetSalesDocumentOnClose: null,
  ignoreCurrent: null,
  payButtonClicked: false,
  paymentLimits: [],
  paymentEditIndex: null,
  paymentDecimalEdit: null,
  paymentSelected: '',
  showPaymentsInput: {},
  chargeConfirmed: null,
  transactionConfirmed: null,
  currency: {},
  usingManualStoreCredit: false,
  waitingForTerminal: false,
};

export default (localState = initialState, { type, payload }) => {
  switch (type) {
    case actionTypes.RESET_PAYMENTS:
    case loginTypes.TYPE_LOGOUT:
    case loginTypes.TYPE_LOGOUT_SOFT:
      return initialState;
    case actionTypes.SET_MANUAL_STORE_CREDIT:
      return {
        ...localState,
        usingManualStoreCredit: payload,
      };
    case actionTypes.SET_PAYMENT_SELECTED:
      return {
        ...localState,
        payments: R.reject(
          payment =>
            !Number(payment.amount) && payment.key !== payload && !payment.paid,
          localState.payments,
        ),
        // Should not be able to select a 'paid' payment since that allows editing the quantity and/or deleting the payment
        paymentSelected: localState.payments[payload]?.paid ? '' : payload,
      };
    case actionTypes.SET_PAYMENT:
      // eslint-disable-next-line no-param-reassign
      payload = {
        ...localState.payments,
        [payload.key]: payload,
      };

    // fallthrough
    case actionTypes.SET_PAYMENTS:
      return {
        ...localState,
        payments: payload,
        // Should not be able to select a 'paid' payment since that allows editing the quantity and/or deleting the payment
        paymentSelected:
          payload[localState.paymentSelected]?.paid ||
          !payload[localState.paymentSelected]
            ? ''
            : localState.paymentSelected,
      };
    case actionTypes.SET_SHOW_PAYMENT_INPUT:
      return {
        ...localState,
        showPaymentsInput: payload,
      };
    case actionTypes.SET_WAITING_FOR_TERMINAL:
      return {
        ...localState,
        waitingForTerminal: payload,
      };
    case actionTypes.SET_CHARGE_CONFIRMED:
      return {
        ...localState,
        chargeConfirmed: payload,
      };

    case actionTypes.SET_TRANSACTION_CONFIRMED:
      return {
        ...localState,
        transactionConfirmed: payload,
      };

    case actionTypes.SET_SALES_DOCUMENT:
      return {
        ...localState,
        salesDocument: payload,
      };
    case actionTypes.SET_PAY_BUTTON_CLICKED:
      return {
        ...localState,
        payButtonClicked: payload,
      };
    case actionTypes.MARK_FOR_PROCESSING:
      return R.evolve({
        payments: { [payload]: R.assoc('shouldProcess', true) },
      })(localState);
    case actionTypes.UNMARK_FROM_PROCESSING:
      return R.evolve({
        payments: { [payload]: R.assoc('shouldProcess', false) },
      })(localState);
    case actionTypes.SET_PAYMENT_DECIMAL_EDIT:
      return {
        ...localState,
        paymentDecimalEdit: payload,
      };
    case actionTypes.SET_PAYMENT_EDIT_INDEX:
      return {
        ...localState,
        paymentEditIndex: payload,
      };
    case actionTypes.SET_STATE:
      return payload;
    case actionTypes.UPDATE_PAYMENTS_TOTAL:
      return {
        ...localState,
        total: payload,
      };
    case actionTypes.UPDATE_CURRENCY:
      return {
        ...localState,
        currency: payload,
      };

    case actionTypes.SET_BOARD_CARD:
      return R.assocPath(
        ['payments', payload.key, 'boardCard'],
        payload.flag,
        localState,
      );
    case actionTypes.SET_CARD_TOKEN_IN_PAYMENT:
      return R.assocPath(
        ['payments', payload.key, 'cardToken'],
        payload.cardToken,
        localState,
      );
    case actionTypes.REMOVE_CARD_TOKEN_IN_PAYMENT:
      return R.dissocPath(['payments', payload.key, 'cardToken'], localState);
    default:
      return localState;
  }
};
/*
 * UI Behaviour
 */
export const getIsCurrentSaleAReturn = state =>
  state.Payments.isCurrentSaleAReturn;
export const getTotal = state => state.Payments.total;
export const getShowCashInput = state => state.Payments.showCashInput;
export const getOriginalPayments = state => state.Payments.originalPayments;
export const getReturnPayments = state => state.Payments.returnPayments;
export const getTransactionConfirmed = state =>
  state.Payments.transactionConfirmed;
export const getChargeConfirmed = state => state.Payments.chargeConfirmed;
export const getPaymentEditIndex = state => state.Payments.paymentEditIndex;
export const getPaymentDecimalEdit = state => state.Payments.paymentDecimalEdit;
export const getResetSalesDocumentOnClose = state =>
  state.Payments.resetSalesDocumentOnClose;
export const getIgnoreCurrentOngoingSale = state =>
  state.Payments.ignoreCurrent;
export const getHasPayButtonBeenClicked = state =>
  state.Payments.payButtonClicked;
export const getPaymentEditValue = state => state.Payments.paymentEditValue;

/*
 * Payments
 */
export const getPayments = state => state.Payments?.payments || {};
export const getShowPaymentInput = state => state.Payments.showPaymentsInput;
export const getAllPayments = createSelector(getPayments, pmts =>
  Object.entries(pmts).map(([key, v]) => ({ ...v, key })),
);
export const getIsWaitingForTerminal = state =>
  state.Payments.waitingForTerminal;
export const getCardPayments = createSelector(getAllPayments, payments =>
  payments.filter(payment => payment.type === 'CARD'),
);
export const getCardPaymentKeys = createSelector(
  getCardPayments,
  cardPayments => cardPayments.map(card => card.key),
);
export const getUnpaidCardPayments = createSelector(getCardPayments, pmts =>
  pmts.filter(p => !p.paid),
);
export const getPaidCardPayments = createSelector(getCardPayments, pmts =>
  pmts.filter(p => p.paid),
);
export const getExistingInReduxHookPayments = hookPayments => state => {
  const reduxCardPayments = getCardPayments(state);
  return (
    hookPayments
      .map(({ key }) => {
        return reduxCardPayments.find(p => p.key === key);
      })
      // filter out cards that were not found and resulted in undefined
      .filter(card => card)
  );
};
export const getCardPaymentsForIntegration = integration => state =>
  Object.entries(getPayments(state))
    .map(([key, v]) => ({ ...v, key }))
    .filter(payment => {
      const currentInt =
        !payment.paymentIntegration &&
        payment.type === 'CARD' &&
        integration === getActivePaymentIntegration(state);
      const forcedInt = payment.paymentIntegration === integration;
      return currentInt || forcedInt;
    });

export const getIsUsingManualStoreCredit = state =>
  state.Payments.usingManualStoreCredit;

export const getPaymentCumulativeSumOfRoundings = state =>
  state.Payments.cumulativeSumOfRoundings;

export const getPaymentSelected = state => state.Payments.paymentSelected;

export const getIsSpecialPaymentMenuSelected = createSelector(
  getPaymentSelected,
  paymentSelected => ({
    isCheckMenuSelected: /check(-\d+)?/.test(paymentSelected),
    isGiftCardMenuSelected: /giftcard(-\d+)?/.test(paymentSelected),
    isCustomPaymentTypeMenuSelected: /custom-payment-type(-\d+)?/.test(
      paymentSelected,
    ),
  }),
);

export const getPaymentLimits = state => state.Payments.paymentLimits;
export const getLastPayment = state => {
  const paymentsKeys = Object.keys(state.Payments.payments);
  const keysCount = paymentsKeys.length;
  const lastPayment =
    keysCount > 0 ? state.Payments.payments[paymentsKeys[keysCount - 1]] : {};
  return { lastPayment, lastKey: paymentsKeys[keysCount - 1] };
};

export const getIsPaymentWithCashOnly = createSelector(
  state => Object.values(getPayments(state)),
  payments => payments.length && payments.every(({ type }) => type === 'CASH'),
);

export const getIsPaymentWithCardOnly = createSelector(
  state => Object.values(getPayments(state)),
  payments => payments.length && payments.every(({ type }) => type === 'CARD'),
);

/*
 * Sale
 */
export const getSalesDocument = state => state.Payments.salesDocument;
export const getCurrentSalesDocument = state =>
  state.Payments.currentSalesDocument;
export const getSalesDocumentReturnedPayments = state =>
  getSalesDocument(state).returnedPayments;
export const getSalesDocumentAdvancePayment = createSelector(
  state => getSalesDocument(state),
  saleDocument => saleDocument?.advancePayment,
);
export const getUnroundedTotal = state => Number(state.Payments.total);
export const getRoundedTotal = createSelector(
  state => getUnroundedTotal(state),
  state => getPosRoundCashFunction(state),
  (unroundedTotal, round) => round(unroundedTotal),
);

export const getPaymentsTotal = createSelector(
  state => getUnroundedTotal(state),
  state => state.Payments.currency,
  (total, currency) => {
    return total / Number(currency?.rate ?? 1);
  },
);

export function getIsAReturn(state) {
  return getIsCurrentSaleAReturn(state) || getTotal(state) < 0;
}

export const getIsEligibleForCashRounding = createSelector(
  getPayments,
  getOriginalPayments,
  getIsAReturn,
  (payments, originalPayments, isReturn) => {
    const paymentsArray = Object.values(payments);
    if (!paymentsArray.length) {
      return false;
    }

    const hasCashPayments = paymentsArray.some(
      payment => payment.type === 'CASH',
    );
    if (hasCashPayments) {
      return true;
    }
    if (isReturn) {
      return false;
    }
    const wasPaidWithCashBefore = originalPayments.some(
      payment => payment.type === 'CASH',
    );
    return wasPaidWithCashBefore;
  },
);

export const getPaymentsCurrency = state => {
  const defaultCurrency = getDefaultCurrency(state);
  return (
    state.Payments.currency || getCurrencyByCode(defaultCurrency)(state) || {}
  );
};

export const getTip = createSelector(
  state => getPaymentsCurrency(state),
  state => Object.values(state.Payments.payments),
  ({ code, rate }, payments) => {
    // On picked up payments, the `type` is `PAID` while on new ones it's `TIP`
    return payments
      .filter(p => p.type === 'TIP' || p.caption === 'TIP')
      .map(pmt => {
        if (code !== pmt.currencyCode) {
          const multiplier = Number(
            round((pmt.currencyRate ?? 1) / Number(rate ?? 1)),
          );

          return Math.abs(pmt.amount) * multiplier;
        }

        return Math.abs(pmt.amount);
      })
      .reduce(add, 0);
  },
);

export const getPaymentsSum = createSelector(
  state => getPaymentsCurrency(state),
  state => Object.values(state.Payments.payments),
  ({ code, rate }, payments) => {
    return payments
      .map(pmt => {
        if (code !== pmt.currencyCode) {
          const multiplier = Number(
            Number(pmt.currencyRate ?? 1) / Number(rate ?? 1),
          );

          return Number(pmt.amount) * multiplier;
        }

        return Number(pmt.amount);
      })
      .reduce(add, 0);
  },
);

export const getBalance = createSelector(
  state => getPaymentsSum(state),
  state => getPaymentsTotal(state),
  (sum, total) => {
    const diff = Number(sum - round(total, 2)) || 0;
    return Math.abs(diff) < 10 ** -4 ? 0 : diff;
  },
);

export const getRounding = createSelector(
  state => getChange(state),
  state => getBalance(state),
  (change, balance) => {
    return balance - change;
  },
);

/**
 * The change that would be given back to the customer if this was a full payment
 * NB: This *will* return negative amounts in case of underpayment, but
 * negative change should not *actually* be given out!
 * Instead, a negative change signifies that the customer has underpaid by a
 * larger amount than can be explained by cash rounding
 */
export const getChange = createSelector(
  state => getPosRoundCashFunction(state),
  getBalance,
  (roundCash, balance) => {
    const change = -roundCash(-balance, { type: 'change' });
    // Avoid negative 0. Formatting functions that use toLocaleString will preserve
    // the minus sign and "-0.00" is not something we want to show to the user
    return change === 0 ? Math.abs(change) : change;
  },
);

export const getCustomer = state => state.Payments.customer;

/**
 * True if the current customer (either explicitly passed to openPaymentModal or passively selected in the cart)
 * is the default customer
 *
 * TODO: Better name?
 *       Includes 'Payment' to emphasize that it's a payment selector, and would not be appropriate
 *       to use outside of the payment context
 */
export const getPaymentIsDefaultCustomer = createSelector(
  getCustomer,
  getDefaultCustomer,
  (customer, defaultCustomer) =>
    Number(customer.id) === Number(defaultCustomer.id),
);

export const getCashPayment = createSelector(
  state => getPayments(state),
  state => getPaymentsCurrency(state),
  (payments, { code }) => payments[`${code}-cash`],
);
/*
 * External
 */
export const getEmployeeID = state => getLoggedInEmployeeID(state);

/*
 * Buttons
 */
export const getIsPaymentDisabled = state => (type, serial) => {
  const paymentLimit = getPaymentLimits(state).find(
    limit =>
      limit.type === type && [undefined, !!limit.serial].includes(serial),
  );
  return paymentLimit && paymentLimit.amount < 0;
};
export const getIsSerialGiftcardDisabled = state => {
  const paymentLimit = getPaymentLimits(state).find(
    limit => limit.serial && limit.type === 'GIFTCARD',
  );

  return paymentLimit && paymentLimit.amount < 0;
};

export const getIsGiftcardDisabled = state => {
  const paymentLimit = getPaymentLimits(state).find(
    limit => !limit.serial && limit.type === 'GIFTCARD',
  );
  return paymentLimit && paymentLimit.amount < 0;
};

/*
 * DEBUG
 */
export const getPaymentsState = state => state;

/**
 * Generate unique payment key based on given type
 *
 * @param {string} - card payment type
 */

export const getNewPaymentKeyForType = type =>
  createSelector(getPayments, state => {
    const usedNumbers = Object.keys(state)
      .filter(k => k.includes(type))
      .map(p => p.replace(type, ''))
      .map(Number)
      // eslint-disable-next-line no-restricted-globals
      .filter(n => !isNaN(n));
    const maxNumber = Math.max(0, ...usedNumbers);
    return `${type}${maxNumber + 1}`;
  });
export const getCurrentSalesDocPaymentsCombo = createSelector(
  state => getOriginalPayments(state),
  state => getReturnPayments(state),
  state => getPayments(state),
  (originalPayments, returnPayments, currentPayments) =>
    originalPayments.map((pmt, i) => {
      // Handling for old payments that were saved before originalPaymentID attribute was introduced
      if (returnPayments.every(p => !Number(p.attributes?.originalPaymentID))) {
        const spentPreviouslyForThisCard = returnPayments
          .filter(p => p.cardNumber === pmt.cardNumber && p.type === pmt.type)
          .map(p => -Number(p.sum))
          .reduce(add, 0);
        const spentCurrentlyForThisCard = Object.values(currentPayments)
          .filter(p => p?.original?.paymentID === pmt.paymentID)
          .map(p => -Number(p.amount))
          .reduce(add, 0);

        const spent = Math.max(
          0,
          spentPreviouslyForThisCard + spentCurrentlyForThisCard,
        );
        return {
          ...pmt,
          spent,
          remaining: Math.min(pmt.sum - spent, Number(pmt.sum)),
        };
      }

      const spentPreviouslyForThisPayment = returnPayments
        .filter(p => p.attributes?.originalPaymentID === pmt.paymentID)
        .map(p => -Number(p.sum))
        .reduce(add, 0);
      const spentCurrentlyForThisPayment = Object.values(currentPayments)
        .filter(p => p.original?.paymentID === pmt.paymentID)
        .map(p => -Number(p.amount))
        .reduce(add, 0);

      const spent =
        spentPreviouslyForThisPayment + spentCurrentlyForThisPayment;

      return {
        ...pmt,
        spent,
        remaining: Math.min(pmt.sum - spent, Number(pmt.sum)),
      };
    }),
);
/** @deprecated unused */
export const getUnreturnedPaymentsTotal = state => {
  const pmts = getCurrentSalesDocPaymentsCombo(state);
  return pmts.map(p => p.remaining).reduce(add, 0);
};

const getPaymentsExceededTotalBase = createSelector(
  getPaymentsTotal,
  getBalance,
  (total, balance) => Math.sign(-balance) === -Math.sign(total),
);

export const getPaymentsExceededTotal = createOverrideSelector(
  'getPaymentsExceededTotal',
  getPaymentsExceededTotalBase,
);

export const getCardPaymentsSum = createSelector(
  getPaymentsCurrency,
  getPayments,
  ({ rate }, paymentDict) => {
    const payments = Object.values(paymentDict);
    const cardPaymentsSum =
      payments
        .filter(p => p.type === 'CARD')
        .map(p => Number(p.amount) * (p.currencyRate ?? 1))
        .reduce(R.add, 0) / rate;
    return cardPaymentsSum;
  },
);

export const getCashAmountToPay = createSelector(
  getPaymentsTotal,
  getPaymentsCurrency,
  getPayments,
  (total, { code: currency, rate }, paymentDict) => {
    const payments = Object.values(paymentDict);
    // Figure out the amount to pay, based on the total and sum of all other payments
    // (ignoring the current CASH payment because it will be overwritten)
    const otherPaymentsSum =
      payments
        .filter(
          p =>
            p.currencyCode !== currency ||
            (p.type === 'CASH' && p.original?.paymentID) ||
            // Check for change helps the receipt not to get overpaid if Change is converted to Cash
            (p.type !== 'CASH' && p.type !== 'CHANGE'),
        )
        .map(p => Number(p.amount) * (p.currencyRate ?? 1))
        .reduce(R.add, 0) / rate;
    return total - otherPaymentsSum;
  },
);

const getCashPaymentsExceededTotalBase = createSelector(
  getPaymentsTotal,
  getCashAmountToPay,
  (total, toPay) => Math.sign(toPay) === -Math.sign(total),
);

export const getCashPaymentsExceededTotal = createOverrideSelector(
  'getCashPaymentsExceededTotal',
  getCashPaymentsExceededTotalBase,
);

export const getIsPartialPaymentAllowed = createSelector(
  getSalesDocument,
  getHasPayButtonBeenClicked,
  getSalesDocumentAdvancePayment,
  (salesDocument, payButtonClicked, outstandingAmount) => {
    // Orders, waybills and invwaybills can save with any payment amount (incl none)
    return (
      (['ORDER', 'WAYBILL'].includes(salesDocument.type) &&
        !payButtonClicked) ||
      (salesDocument.type === 'INVWAYBILL' &&
        (!outstandingAmount || Number(outstandingAmount) === 0)) ||
      salesDocument.type === 'INVOICE'
    );
  },
);
